import { MediaDropPill } from '@arthelper/ui';
import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Button, Label, Popover, PopoverContent, PopoverTrigger, Textarea } from '@artstorefronts/ui';
import { Smile } from 'lucide-react';
import { EmojiPickerPopover } from './EmojiPickerPopover';
import { imagesFromClipboard } from './DmAttachments';
import { useDmAttachments } from '~/hooks/useDmAttachments';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { RecipientPicker, RecipientUser } from '~/components/messages/RecipientPicker';
import { CREATE_DIRECT_MESSAGE_MUTATION } from '~/graphql/direct-messages';
import { MESSAGEABLE_USERS_QUERY } from '~/graphql/connection';
import { useAnalytics } from '~/context/analytics';
import { useMessagingDock } from '~/context/messagingDock';

const MAX_CONTENT_LENGTH = 5000;
const MAX_RECIPIENTS = 30;

interface Props {
  prefillRecipientAsfId?: string;
  prefillRecipient?: RecipientUser;
  prefillBody?: string;
}

/**
 * Compact "new message" composer for the messaging dock. Reuses the same
 * `RecipientPicker` + `createDirectMessage` mutation as the full-page
 * `/messages/new` route so behaviour stays consistent.
 */
export function DockComposeView({ prefillRecipientAsfId, prefillRecipient, prefillBody }: Props) {
  const { t } = useTranslation('social');
  const {
    amplitude: { track },
  } = useAnalytics();
  const { openThread, setComposeDraft } = useMessagingDock();

  const [recipients, setRecipients] = useState<RecipientUser[]>(() =>
    prefillRecipient?.asfId ? [prefillRecipient] : []
  );
  const [content, setContent] = useState(() => prefillBody ?? '');
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const contentRef = useRef<HTMLTextAreaElement | null>(null);
  const hasAutoFocusedRef = useRef(false);
  const didSeedPrefillRecipientRef = useRef(!!prefillRecipient?.asfId);

  // openCompose can update prefillBody without remounting this view.
  useEffect(() => {
    if (prefillBody == null) return;
    setContent(prefillBody);
  }, [prefillBody]);

  // Mirror the live textarea value up to the dock so "open in full page"
  // hands off what the user actually typed, not the stale initial prefill.
  useEffect(() => {
    setComposeDraft(content);
  }, [content, setComposeDraft]);

  // Seed To once from an explicit recipient (say-hi). Do not re-apply after
  // the user clears the chip — that would make To unremovable.
  useEffect(() => {
    if (!prefillRecipient?.asfId || didSeedPrefillRecipientRef.current) return;
    didSeedPrefillRecipientRef.current = true;
    setRecipients([prefillRecipient]);
  }, [prefillRecipient]);

  const prefillQuery = useQuery(MESSAGEABLE_USERS_QUERY, {
    // Empty query returns recent messageable users; API does not match asfId.
    variables: { query: '', first: 50 },
    skip: !prefillRecipientAsfId || recipients.length > 0 || didSeedPrefillRecipientRef.current,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (!prefillRecipientAsfId || prefillQuery.loading || recipients.length > 0 || didSeedPrefillRecipientRef.current)
      return;
    const edges = prefillQuery.data?.messageableUsers?.edges || [];
    const match = edges.map((e: any) => e?.node).find((u: any) => u?.asfId === prefillRecipientAsfId);
    if (match) {
      didSeedPrefillRecipientRef.current = true;
      setRecipients([match]);
    }
  }, [prefillRecipientAsfId, prefillQuery.data, prefillQuery.loading, recipients.length]);

  useEffect(() => {
    if (hasAutoFocusedRef.current) return;
    if (!prefillRecipientAsfId || recipients.length === 0) return;
    contentRef.current?.focus();
    hasAutoFocusedRef.current = true;
  }, [prefillRecipientAsfId, recipients.length]);

  const [createDirectMessage, { loading: submitting }] = useMutation(CREATE_DIRECT_MESSAGE_MUTATION);
  const { addFiles, clear, uploading, imageIds, fileAttachments, dropProps } = useDmAttachments();
  const dropScope = useRef<HTMLFormElement>(null);

  const contentLength = content.length;
  const canSubmit =
    recipients.length > 0 &&
    !submitting &&
    !uploading &&
    contentLength <= MAX_CONTENT_LENGTH &&
    (contentLength > 0 || imageIds.length > 0 || fileAttachments.length > 0);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const recipientCount = recipients.length;
    const contentLen = content.length;
    track('dm_new_message_send_clicked', {
      source: 'dock',
      recipient_count: recipientCount,
      content_length: contentLen,
    });
    try {
      const { data } = await createDirectMessage({
        variables: {
          input: {
            recipientUserIds: recipients.map((u) => u.asfId),
            // DMs dedupe per recipient set, so subjects never surface a
            // second time. We keep the column on Message for the email
            // channel but always send null for in-app DMs.
            subject: null,
            content,
            imageIds,
            files: fileAttachments,
          },
        },
      });
      const payload = data?.createDirectMessage;
      if (!payload?.success || !payload.message?.id) {
        const err = payload?.error || 'UNKNOWN_ERROR';
        if (err === 'CONTENT_OUT_OF_RANGE') {
          toast.error(t('dm.error-content-length', 'Message must be between 1 and 5000 characters'));
        } else if (err === 'NOT_CONNECTED') {
          toast.error(t('dm.error-not-connected', 'You can only message your connections'));
        } else if (err === 'FEATURE_DISABLED') {
          toast.error(t('dm.error-feature-disabled', 'Direct messaging is not yet available'));
        } else {
          toast.error(t('dm.error-generic', 'Could not send message: {{error}}', { error: err }));
        }
        track('dm_new_message_send_failed', {
          source: 'dock',
          recipient_count: recipientCount,
          content_length: contentLen,
          error_code: String(err),
        });
        return;
      }
      track('dm_new_message_send_success', {
        source: 'dock',
        recipient_count: recipientCount,
        content_length: contentLen,
      });
      clear();
      openThread(payload.message.id, { source: 'compose_success' });
    } catch (err: any) {
      const message: string = err?.message || '';
      if (/connected/i.test(message)) {
        toast.error(t('dm.error-not-connected', 'You can only message your connections'));
      } else {
        toast.error(message || t('dm.error-generic-fallback', 'Failed to send message'));
      }
      track('dm_new_message_send_failed', {
        source: 'dock',
        recipient_count: recipientCount,
        content_length: contentLen,
        error_code: 'EXCEPTION',
      });
    }
  }

  return (
    <form ref={dropScope} onSubmit={handleSubmit} className="relative flex-1 min-h-0 flex flex-col">
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        <div>
          <Label htmlFor="dock-recipients" className="text-xs">
            {t('dm.to-label', 'To')}
          </Label>
          <RecipientPicker value={recipients} onChange={setRecipients} max={MAX_RECIPIENTS} />
          <p className="text-[11px] text-gray-500 mt-1">
            {t('dm.recipients-help', 'Only people connected to you can be added')}
          </p>
        </div>

        <div>
          <Label htmlFor="dock-content" className="text-xs">
            {t('dm.message-label', 'Message')}
          </Label>
          <Textarea
            ref={contentRef}
            id="dock-content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onPaste={(e) => {
              const files = imagesFromClipboard(e);
              if (files.length) {
                e.preventDefault();
                addFiles(files);
              }
            }}
            rows={6}
            placeholder={t('dm.message-placeholder', 'Write your message...')}
            maxLength={MAX_CONTENT_LENGTH}
            className="text-sm resize-none"
          />
          <div className="flex items-center justify-between mt-1">
            <div className="flex items-center gap-1">
              <Popover open={emojiPickerOpen} onOpenChange={setEmojiPickerOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    aria-label={t('dm.insert-emoji', 'Insert emoji')}
                    className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  >
                    <Smile className="size-4" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-72" align="start" side="top" sideOffset={6}>
                  <EmojiPickerPopover
                    textareaRef={contentRef}
                    value={content}
                    onChange={setContent}
                    onClose={() => setEmojiPickerOpen(false)}
                  />
                </PopoverContent>
              </Popover>
              <MediaDropPill
                {...dropProps}
                label={t('dm.attachments.add', 'Attach a file')}
                chipsPlacement="above"
                dropScope={dropScope}
              />
            </div>
            <span className={`text-[11px] ${contentLength > MAX_CONTENT_LENGTH ? 'text-red-600' : 'text-gray-500'}`}>
              {contentLength} / {MAX_CONTENT_LENGTH}
            </span>
          </div>
        </div>
      </div>

      <div className="flex-shrink-0 border-t border-gray-200 px-3 py-2 flex items-center justify-end gap-2">
        <Button type="submit" size="sm" disabled={!canSubmit}>
          {submitting ? t('dm.sending', 'Sending...') : t('dm.send', 'Send')}
        </Button>
      </div>
    </form>
  );
}
